import {useState, useEffect, useMemo, Fragment} from 'react';
import {
    Typography, CircularProgress, Alert, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, Paper, Card, CardContent, Box, IconButton,
    Tooltip, Divider, Pagination
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const DOCS_PER_PAGE_TARGET = 100;

// Helper: libellé Type basé sur classification.type
const getTypeLabel = (doc) => {
    const t = (doc?.classification?.type ?? '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // retire accents

    if (t === 'recu') return 'Certification TEJ Reçu';
    if (t === 'emis') return 'Certification TEJ Émis';
    if (t === 'declaration') return 'Déclaration Employeur';
    return '—';
};

const DocumentsEnAttente = () => {
    const [allDocs, setAllDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageIndex, setPageIndex] = useState(0); // 0-based pour l'UI

    const fetchAllDocs = async () => {
        setLoading(true);
        setError(null);
        try {
            const acc = [];
            let p = 1;
            let guard = 0;
            while (true) {
                const res = await axios.get('/api/comptable/documents', {
                    params: { page: p, limit: 100, isProcessed: false }
                });
                const payload = res.data;
                const docs = Array.isArray(payload) ? payload : (payload.data || []);
                acc.push(...docs);

                const meta = Array.isArray(payload)
                    ? { hasNext: false, nextPage: null }
                    : (payload.meta || { hasNext: false, nextPage: null });

                if (!meta.hasNext) break;
                p = meta.nextPage || (p + 1);
                guard += 1;
                if (guard > 1000) break; // garde-fou
            }
            setAllDocs(acc);
            setPageIndex(0); // revenir à la 1ère page
        } catch (e) {
            console.error(e);
            setError("Une erreur s'est produite lors du chargement des documents.");
        } finally {
            setLoading(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    useEffect(() => { fetchAllDocs(); }, []);

    // Groupement par client
    const groups = useMemo(() => {
        const map = {};
        for (const doc of allDocs) {
            const client = doc.clientId && typeof doc.clientId === 'object' ? doc.clientId : {};
            const clientId = client._id || doc.clientId || 'unknown';
            const clientName =
                (client.raisonSociale && client.raisonSociale.trim() !== '')
                    ? client.raisonSociale
                    : [client.nom, client.prenom].filter(Boolean).join(' ').trim() || '—';

            if (!map[clientId]) map[clientId] = { clientId, clientName, documents: [] };
            map[clientId].documents.push(doc);
        }

        // tri alphabétique + tri docs par createdAt desc
        return Object.values(map)
            .map(g => ({
                ...g,
                documents: [...g.documents].sort((a, b) => {
                    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return tb - ta;
                })
            }))
            .sort((a, b) =>
                (a.clientName || '—').localeCompare(b.clientName || '—', 'fr', { sensitivity: 'base' })
            );
    }, [allDocs]);

    // Ajout d’un index local (par client)
    const clientDocsWithLocalIndex = useMemo(() => {
        return groups.map(group => {
            const docsWithIndex = group.documents.map((doc, idxWithinClient) => ({
                ...doc,
                localIndex: idxWithinClient + 1
            }));
            return {
                clientId: group.clientId,
                clientName: group.clientName,
                documents: docsWithIndex
            };
        });
    }, [groups]);

    const totalDocs = useMemo(
        () => clientDocsWithLocalIndex.reduce((sum, g) => sum + g.documents.length, 0),
        [clientDocsWithLocalIndex]
    );

    // Construire les pages sans couper un client (≈100 docs/page)
    const pages = useMemo(() => {
        const result = [];
        let current = { clients: [], docsCount: 0 };

        for (const g of clientDocsWithLocalIndex) {
            const count = g.documents.length;

            if (current.docsCount > 0 && (current.docsCount + count) > DOCS_PER_PAGE_TARGET) {
                result.push(current);
                current = { clients: [], docsCount: 0 };
            }
            current.clients.push(g);
            current.docsCount += count;
        }
        if (current.clients.length) result.push(current);
        return result;
    }, [clientDocsWithLocalIndex]);

    const currentPage = pages[pageIndex] || { clients: [], docsCount: 0 };

    // ---- UI ----
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return (
            <Box py={4}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Box
                display="flex"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                gap={2}
                flexDirection={{ xs: 'column', sm: 'row' }}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5">Documents en attente (sans couper les clients)</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Page {pageIndex + 1} / {pages.length || 1} • {currentPage.docsCount} doc(s) affiché(s) • {totalDocs} au total
                    </Typography>
                </Box>
                <Tooltip title="Rafraîchir">
                    <IconButton onClick={fetchAllDocs}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {currentPage.clients.length > 0 ? (
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer component={Paper} elevation={0}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={80}><strong>#</strong></TableCell>
                                        <TableCell><strong>Nom du document</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Classification</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentPage.clients.map((g) => (
                                        <Fragment key={g.clientId}>
                                            {/* Bandeau client */}
                                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                {/* ⚠️ 4 colonnes dans le head => colSpan=4 */}
                                                <TableCell colSpan={4}>
                                                    <strong>{g.clientName || 'Client inconnu'}</strong> — {g.documents.length} doc(s)
                                                </TableCell>
                                            </TableRow>

                                            {/* Lignes documents */}
                                            {g.documents.map((doc) => (
                                                <TableRow key={doc._id} hover>
                                                    <TableCell>{doc.localIndex}</TableCell>
                                                    <TableCell>{doc.originalName || doc.fileName || '—'}</TableCell>

                                                    {/* Type: basé sur classification.type */}
                                                    <TableCell>{getTypeLabel(doc)}</TableCell>

                                                    <TableCell>
                                                        {doc.classification
                                                            ? `${doc.classification.type} — ${doc.classification.annee}/${String(doc.classification.mois).padStart(2, '0')}`
                                                            : 'Non classé'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box p={2} display="flex" justifyContent="center">
                            <Pagination
                                count={pages.length}
                                page={pageIndex + 1}
                                onChange={(_, v) => {
                                    setPageIndex(v - 1);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                color="primary"
                                shape="rounded"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Alert severity="info">Aucun document en attente pour le moment.</Alert>
            )}
        </Box>
    );
};

export default DocumentsEnAttente;
